import React, { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { BirdiTheme, breakpoints, fonts } from '@birdi/theme';
import SearchSVG from '@birdi/icons/search.svg';
import ChevronDownSVG from '@birdi/icons/chevron-down.svg';
import { GlobalSearchOverlay } from './global-search-overlay';
import { InitialScope } from './use-global-search';
import { SEARCH_PLACEHOLDER } from './search-copy';

const isEditableTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.isContentEditable);

export interface GlobalSearchButtonProps {
  /**
   * `wide` — a full-width search pill for roomy headers (dashboard); the
   * overlay expands straight down from the pill, matching its width.
   * `compact` — a small pill button for crowded headers (map); the overlay
   * opens at full screen width instead.
   * `caret` — a chevron beside the map name (map header); the overlay grows
   * from the chevron to full screen.
   */
  variant?: 'wide' | 'compact' | 'caret';
  /**
   * Called when the overlay opens. Return the extent of the map/mission the
   * user is on to seed the viewport filter, and the map's id so it is pinned
   * first on the initial list (PRD rules). Null for none (dashboard).
   */
  getInitialScope?: () => InitialScope | null;
}

/**
 * Top-nav entry point for Global Search. Also binds ⌘K / Ctrl+K while
 * mounted. The overlay only mounts while open, so Cesium and search state
 * are torn down on close.
 */
export const GlobalSearchButton = ({
  variant = 'compact',
  getInitialScope,
}: GlobalSearchButtonProps) => {
  const [open, setOpen] = useState(false);
  const [initialScope, setInitialScope] = useState<InitialScope | null>(null);
  const openSearch = useCallback(() => {
    setInitialScope(getInitialScope?.() ?? null);
    setOpen(true);
  }, [getInitialScope]);
  const anchorRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        if (isEditableTarget(event.target) && !event.metaKey && !event.ctrlKey)
          return;
        event.preventDefault();
        openSearch();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openSearch]);

  const isMac =
    typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);

  return (
    <>
      {variant === 'caret' ? (
        <button
          ref={anchorRef}
          type="button"
          css={caretCss}
          onClick={openSearch}
          data-testid="global-search-open"
          aria-label={SEARCH_PLACEHOLDER}
          title={`${SEARCH_PLACEHOLDER} (${isMac ? '⌘K' : 'Ctrl K'})`}
        >
          <ChevronDownSVG aria-hidden />
        </button>
      ) : (
        <button
          ref={anchorRef}
          type="button"
          css={variant === 'wide' ? widePillCss : compactPillCss}
          onClick={openSearch}
          data-testid="global-search-open"
          aria-label={SEARCH_PLACEHOLDER}
        >
          <SearchSVG />
          <span css={variant === 'wide' ? widePlaceholderCss : undefined}>
            {variant === 'wide' ? SEARCH_PLACEHOLDER : 'Search'}
          </span>
          <kbd css={kbdCss}>{isMac ? '⌘K' : 'Ctrl K'}</kbd>
        </button>
      )}
      {open && (
        <GlobalSearchOverlay
          onClose={() => setOpen(false)}
          anchorEl={anchorRef.current}
          initialBbox={initialScope?.bbox ?? null}
          initialMapId={initialScope?.mapId ?? null}
        />
      )}
    </>
  );
};

const pillBaseCss = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid ${theme.midGrey};
  border-radius: 5px; /* matches the rest of the UI, not a pill */
  background: ${theme.mono0};
  color: ${theme.mono70};
  font-family: ${fonts.body};
  font-size: 0.875rem;
  cursor: pointer;
  white-space: nowrap;
`;

/* Dashboard: fills the header from the content column's left edge to the
   nav. HeaderV2 sizes the brand block to end exactly where the dashboard's
   "Hello <name>" container starts, so the field lines up with it and
   everything left of it is logo space. */
const widePillCss = (theme: BirdiTheme) => css`
  ${pillBaseCss(theme)};
  flex: 1 1 auto;
  min-width: 0;
  height: 40px;
  border-radius: 1em; /* matches the dashboard's cards and sidebar */
  padding: 0 1rem;
  margin: 0 1rem 0 0;
  text-align: center;
`;

/* Map header: below the large-desktop breakpoint drop the ⌘K chip so the
   pill takes as little of the toolbar row as possible. */
const compactPillCss = (theme: BirdiTheme) => css`
  ${pillBaseCss(theme)};
  padding: 0.4rem 0.85rem;

  @media (max-width: ${breakpoints.l}) {
    kbd {
      display: none;
    }
  }
`;

/* Map header: a chevron beside the map name. */
const caretCss = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 5px;
  background: none;
  color: ${theme.midnight};
  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover,
  &:focus-visible {
    background: ${theme.dove};
  }
`;

/* Centred between the icon and the ⌘K chip. */
const widePlaceholderCss = (theme: BirdiTheme) => css`
  flex: 1;
  min-width: 0;
  text-align: center;
  color: ${theme.mono40};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const kbdCss = (theme: BirdiTheme) => css`
  margin-left: auto;
  flex-shrink: 0;
  padding: 0.05rem 0.3rem;
  border: 1px solid ${theme.dove};
  border-radius: 999px;
  color: ${theme.mono40};
  font-size: 0.7rem;
`;
